import { z } from "zod";
import { gaPushEvent } from "../base.analytics";
import { sessionStore } from "../../../lib/store";
import type { Api } from "../../../services/api";
import type { QuestionOrigin } from "../../../domains/gpt/hooks/useGptChat";
import type { GptMode } from "../../../domains/gpt/stores/modeStore";
import type { RedactionSummary } from "../../../domains/assistant/redaction";
import type { ConversationEngine } from "../../../domains/assistant/conversationEngine";

enum Event {
  SEARCH = "search",
  GPT_COPY_ANSWER = "gpt_copy_answer",
  GPT_SESSION_STARTED = "juiscigpt_session_started",
  GPT_CONVERSATION_SHARED = "juiscigpt_conversation_shared",
  GPT_CONVERSATION_SHARE_REVOKED = "juiscigpt_conversation_share_revoked",
  GPT_SHARED_CONVERSATION_OPENED = "juiscigpt_shared_conversation_opened",
  GPT_SHARED_CONVERSATION_RESUME_CLICKED = "juiscigpt_shared_conversation_resume_clicked",
  GPT_ROOM_SUGGESTION_SHOWN = "juiscigpt_room_suggestion_shown",
  GPT_ROOM_SUGGESTION_CLICKED = "juiscigpt_room_suggestion_clicked",
  GPT_ROOM_SUGGESTION_DISMISSED = "juiscigpt_room_suggestion_dismissed",
  GPT_ANONYMOUS_QUESTION_ASKED = "gpt_anonymous_question_asked",
  GPT_ANONYMOUS_LIMIT_REACHED = "gpt_anonymous_limit_reached",
  GPT_ANONYMOUS_SIGNUP_CLICKED = "gpt_anonymous_signup_clicked",
  GPT_PATIENT_DATA_REDACTED = "gpt_patient_data_redacted",
}

/** Which daily ceiling stopped a logged-out visitor, as the API names it. */
export type AnonymousGptLimitKind = Extract<
  Api["get"]["/webapp/public/gpt/allowance"]["output"]["body"],
  { state: "refused" }
>["reason"];

// A logged-out visitor's question was accepted (the stream answered 2xx).
// `question_number` is its rank today (1 or 2), from the server's count; null
// when the allowance could not be read. The anonymous funnel's top.
export function gaEventGptAnonymousQuestionAsked(payload: {
  questionNumber: number | null;
}) {
  gaPushEvent(Event.GPT_ANONYMOUS_QUESTION_ASKED, {
    question_number: payload.questionNumber,
  });
}

// A daily ceiling refused a logged-out visitor's question (a 429), or the page
// opened on one already reached. Once per session and reason. Not fired when
// the visitor simply used their last question: nothing was refused. The PRD
// guardrail reads its share of anonymous questions.
export function gaEventGptAnonymousLimitReached(payload: {
  limitKind: AnonymousGptLimitKind;
}) {
  gaPushEvent(Event.GPT_ANONYMOUS_LIMIT_REACHED, {
    limit_kind: payload.limitKind,
  });
}

// The sign-up offer after an anonymous answer was tapped. `limit_kind` is null
// when the visitor still had a question left: they chose to sign up.
export function gaEventGptAnonymousSignupClicked(payload: {
  limitKind: AnonymousGptLimitKind | null;
}) {
  gaPushEvent(Event.GPT_ANONYMOUS_SIGNUP_CLICKED, {
    limit_kind: payload.limitKind,
  });
}

export function gaEventGptSessionStarted() {
  gaPushEvent(Event.GPT_SESSION_STARTED);
}

// The owner's link left: copied on desktop, or handed to the share sheet.
// `share_link` is the server's word on the link: minted by this share
// (`created`), or given out before and shared again (`existing`). A share after
// Stop sharing mints a new link, so it is `created`.
export function gaEventGptConversationShared(payload: {
  conversationId: string;
  engine: ConversationEngine;
  questionCount: number;
  channel: "copy_link" | "share_sheet";
  shareLink: "created" | "existing";
}) {
  gaPushEvent(Event.GPT_CONVERSATION_SHARED, {
    conversation_id: payload.conversationId,
    engine: payload.engine,
    question_count: payload.questionCount,
    channel: payload.channel,
    share_link: payload.shareLink,
  });
}

// The owner tapped Stop sharing and the link stopped working.
export function gaEventGptConversationShareRevoked(payload: {
  conversationId: string;
  engine: ConversationEngine;
}) {
  gaPushEvent(Event.GPT_CONVERSATION_SHARE_REVOKED, {
    conversation_id: payload.conversationId,
    engine: payload.engine,
  });
}

// The shared conversation this tab opened signed out. A resume on it once
// signed in is then a reader who had to sign in on the page, whatever the way
// (the provider popup, `/login`, or a new account through onboarding): the tab,
// and so this session value, survives each of them.
const sharedConversationOpenedSignedOutStore = sessionStore(
  "gpt-shared-conversation-opened-signed-out",
  null,
  z.string().nullable()
);

// A visitor opens a publicly shared conversation (read-only). `isLoggedIn`
// segments returning users from anonymous prospects.
export function gaEventGptSharedConversationOpened(payload: {
  conversationId: string;
  isLoggedIn: boolean;
}) {
  if (!payload.isLoggedIn)
    sharedConversationOpenedSignedOutStore.set(payload.conversationId);
  gaPushEvent(Event.GPT_SHARED_CONVERSATION_OPENED, {
    conversation_id: payload.conversationId,
    is_logged_in: payload.isLoggedIn,
  });
}

// A visitor taps "Resume the discussion" on a shared conversation. Logged-out
// visitors get the signup sheet instead of the composer. `visitor_auth` splits
// the three readers: `signed_out` (the sheet opens), `signed_in_on_page` (opened
// the page signed out, then signed in), `already_signed_in` (no sign-in asked).
export function gaEventGptSharedConversationResumeClicked(payload: {
  conversationId: string;
  isLoggedIn: boolean;
}) {
  const visitorAuth = !payload.isLoggedIn
    ? "signed_out"
    : sharedConversationOpenedSignedOutStore.get() === payload.conversationId
      ? "signed_in_on_page"
      : "already_signed_in";
  // One sign-in counts once: a later visit to the same conversation in this
  // tab arrives signed in.
  if (visitorAuth === "signed_in_on_page")
    sharedConversationOpenedSignedOutStore.set(null);
  gaPushEvent(Event.GPT_SHARED_CONVERSATION_RESUME_CLICKED, {
    conversation_id: payload.conversationId,
    is_logged_in: payload.isLoggedIn,
    visitor_auth: visitorAuth,
  });
}

// ── Room proposal (the agent surfacing a Room mid-conversation) ─────────────
//
// `shown` is the denominator of the conversion rate the Rooms PRD measures
// (card → room joined). The numerator is the EXISTING `room_joined` event,
// stamped `room_discovery_channel = "juiscigpt"` by the card's navigation — a
// property, never a second event, so a GPT-driven join stays comparable to the
// QR / shared-article / related-room channels.
//
// Singular `room_id`, not a list: exactly ONE room ever reaches the user
// (AC-A1c). The plural `room_ids` / `room_count` properties these events used
// to carry are gone with the cap.

// The PRD's common properties, carried by every event in this family so the
// funnels never have to be re-split later.
//
// `is_member` splits ACQUISITION from ACTIVATION on every event, and the two
// are never summed: a member cannot fire `room_joined`, so without this
// property half the feature is invisible in the data.
//
// `trigger` says WHICH path produced the card — the sources the answer cited,
// or the subject the model named. It is passed by the caller, never assumed:
// the two triggers are instrumented separately precisely so the weaker one can
// be killed on data, and a hardcoded value would report every card as
// `topic_match` and make the evidence trigger look like it never fires.
//
// `cited_source_count` is the strength of the evidence behind the proposal —
// whether a card backed by one source converts like one backed by four. It is
// ALWAYS 0 under `topic_match`, because nothing was cited on that path: segment
// on `trigger` before reading it, never average the two together.
//
// `surface` is still INVARIANT and is therefore emitted here rather than passed
// — a parameter with one possible value is a fake choice. It gains a second
// member if the provenance line, cut from v1, is ever re-introduced.
const ROOM_SUGGESTION_COMMON = { surface: "card" } as const;

type RoomSuggestionEventPayload = {
  roomId: string;
  roomName: string;
  isMember: boolean;
  trigger: "evidence" | "topic_match";
  citedSourceCount: number;
  // Which engine wrote the answer that carries the card.
  engine: ConversationEngine;
};

export function gaEventGptRoomSuggestionShown(
  payload: RoomSuggestionEventPayload
) {
  gaPushEvent(Event.GPT_ROOM_SUGGESTION_SHOWN, {
    ...ROOM_SUGGESTION_COMMON,
    room_id: payload.roomId,
    room_name: payload.roomName,
    is_member: payload.isMember,
    trigger: payload.trigger,
    cited_source_count: payload.citedSourceCount,
    engine: payload.engine,
  });
}

export function gaEventGptRoomSuggestionClicked(
  payload: RoomSuggestionEventPayload
) {
  gaPushEvent(Event.GPT_ROOM_SUGGESTION_CLICKED, {
    ...ROOM_SUGGESTION_COMMON,
    room_id: payload.roomId,
    room_name: payload.roomName,
    is_member: payload.isMember,
    trigger: payload.trigger,
    cited_source_count: payload.citedSourceCount,
    engine: payload.engine,
  });
}

export function gaEventGptRoomSuggestionDismissed(
  payload: RoomSuggestionEventPayload
) {
  gaPushEvent(Event.GPT_ROOM_SUGGESTION_DISMISSED, {
    ...ROOM_SUGGESTION_COMMON,
    room_id: payload.roomId,
    room_name: payload.roomName,
    is_member: payload.isMember,
    trigger: payload.trigger,
    cited_source_count: payload.citedSourceCount,
    engine: payload.engine,
  });
}

// A Question sent with at least one Match. Counts and Categories only, never text (INV-05).
export function gaEventGptPatientDataRedacted(summary: RedactionSummary) {
  gaPushEvent(Event.GPT_PATIENT_DATA_REDACTED, {
    match_count: summary.matchCount,
    // Deduplicated, in the canonical Category order.
    categories: summary.categories.join(","),
  });
}

// What the Mode Router did with a question. Only the legacy engine has one:
// the assistant engine has no mode picker, so its question reports no mode
// (null), never an invented "auto" or "general".
type GptQuestionRouting =
  | {
      engine: "legacy";
      // Mode Router (docs/gpt-mode-router.md): what the user picked, what
      // actually ran, whether a mode was forced, and the shadow classifier's
      // confidence (`null` client-side this slice — the DB `routerPrediction`
      // is the eval SoT).
      selectedMode: GptMode;
      resolvedMode: GptMode;
      wasForced: boolean;
      classifierConfidence: number | null;
    }
  | { engine: "assistant" };

function routingParams(routing: GptQuestionRouting) {
  switch (routing.engine) {
    case "legacy":
      return {
        selected_mode: routing.selectedMode,
        resolved_mode: routing.resolvedMode,
        was_forced: routing.wasForced,
        classifier_confidence: routing.classifierConfidence,
      };
    case "assistant":
      return {
        selected_mode: null,
        resolved_mode: null,
        was_forced: null,
        classifier_confidence: null,
      };
    default:
      throw new Error(routing satisfies never);
  }
}

// `engine` says which engine answers, so the assistant rollout splits in GA4
// and Mixpanel. A top-level param: the Mixpanel mirror forwards it as is.
export function gaEventSearchGpt(
  payload: {
    isNewConversation: boolean;
    // How the question reached the agent. Drives BOTH the legacy suggested-vs-
    // manual split AND the precise `question_origin` so follow-up chips
    // (`followup_suggestion`) are separable from landing pills, not just lumped
    // under "suggested".
    origin: QuestionOrigin;
    // The rank of this question in the conversation, 1 for the first.
    questionCount: number;
    question: string;
  } & GptQuestionRouting
) {
  gaPushEvent(Event.SEARCH, {
    engine: payload.engine,
    question: payload.question,
    search: {
      // Back-compat: manual = typed, everything else is a tapped suggestion.
      type: payload.origin === "typed" ? "gpt_manual" : "gpt_suggested",
      // The precise input method, forwarded to Mixpanel via the `search` map.
      origin: payload.origin,
      number: payload.questionCount,
      conversation_history: payload.isNewConversation ? "new" : "old",
      term: payload.question,
      ...routingParams(payload),
    },
  });
}

export function gaEventGptInteraction(payload: {
  engine: ConversationEngine;
  isNewConversation: boolean;
  isSuggestedQuestion: boolean;
  questionCount: number;
  interaction: "copy_answer" | "open_reference" | "ask_new_question";
}) {
  gaPushEvent(Event.GPT_COPY_ANSWER, {
    engine: payload.engine,
    interaction: payload.interaction,
    search: {
      type: payload.isSuggestedQuestion ? "gpt_suggested" : "gpt_manual",
      number: payload.questionCount,
      conversation_history: payload.isNewConversation ? "new" : "old",
    },
  });
}
